export interface PipelineStage {
    name: string
    displayName: string
    tags: Record<string, string>
}

export interface PipelineResponse {
    stages: PipelineStage[]
    fetchedAt: string
}

export interface PromoteRequest {
    sourceStage: string
    targetStage: string
    services?: string[]
    // AB#2091 — waives *overridable* failing preflight checks (unknown health) only.
    force?: boolean
}

// AB#2091 — one ArgoCD app / healthz probe observed during post-promotion verification.
export interface PromoteAppStatus {
    name: string
    syncStatus: string
    healthStatus: string
    operationPhase: string | null
    ready: boolean
}

export interface PromoteProbeStatus {
    name: string
    env: string
    healthy: boolean
    detail: string
}

export interface PromoteResponse {
    message: string
    // AB#2091 — bounded post-promotion verification results (absent on older payloads).
    verified?: boolean
    verificationDetail?: string | null
    apps?: PromoteAppStatus[] | null
    probes?: PromoteProbeStatus[] | null
    syncErrors?: string[] | null
    // AB#2092 — id of the persisted audit record (null when no ops DB is configured).
    promotionId?: string | null
}

export interface PreflightCheck {
    name: string
    passed: boolean
    detail: string
    // AB#2091 — a failing check the server will waive when the request carries force=true.
    overridable?: boolean
}

export interface PreflightResponse {
    canPromote: boolean
    checks: PreflightCheck[]
}

// ---- Image drift (AB#2094) ----

export interface DriftImage {
    name: string
    gitopsTag: string
    runningTags: string[]
    state: 'synced' | 'drifted' | 'unknown'
}

export interface DriftEnv {
    env: string
    images: DriftImage[]
    driftedCount: number
}

export interface DriftResponse {
    envs: DriftEnv[]
    fetchedAt: string
}

// ---- Promotion audit trail + rollback (AB#2092) ----

export interface PromotionImageChange {
    image: string
    beforeTag: string | null
    afterTag: string
}

export interface PromotionRecord {
    id: string
    sourceEnv: string
    targetEnv: string
    actor: string
    forced: boolean
    services: string[] | null
    imageChanges: PromotionImageChange[]
    gitopsCommitSha: string | null
    createdAt: string
    rollbackOf: string | null
}

export interface PromotionHistoryResponse {
    promotions: PromotionRecord[]
    fetchedAt: string
}

export interface RollbackResponse {
    message: string
    promotionId: string | null
    verified?: boolean
    verificationDetail?: string | null
    apps?: PromoteAppStatus[] | null
    syncErrors?: string[] | null
}

// ---- Pending-release queue (AB#2100) ----

export interface PendingQueueEntry {
    prId: number
    kind: 'item' | 'sprint'
    label: string
    state: 'pending' | 'ci-failed' | 'flagged'
    retryCount: number
    ageMinutes: number
    releaseBranch: string
}

export interface PendingQueueResponse {
    items: PendingQueueEntry[]
    fetchedAt: string
}

export type ServiceStatus = 'healthy' | 'degraded' | 'error' | 'unknown'

export interface ServiceHealth {
    name: string
    status: ServiceStatus
    requestsPerMin: number | null
    errorPct: number | null
    p95Ms: number | null
}

export interface RingHealth {
    ring: string
    displayName: string
    services: ServiceHealth[]
    asOf: string
}

export interface HealthResponse {
    rings: RingHealth[]
    fetchedAt: string
}

export interface LandscapeRing {
    ring: string
    displayName: string
    healthStatus: string
    services: ServiceHealth[]
    tenants: TenantRingEntry[]
    isPerRingData: boolean
    healthAsOf: string
}

export interface LandscapeResponse {
    rings: LandscapeRing[]
    fetchedAt: string
}

// AB#2038 — Background jobs (Hangfire dashboard deep links per plane × environment).
export interface BackgroundJobsCard {
    environment: string
    // null when no ingress URL is configured for this (plane, env) — the card then shows only the
    // port-forward fallback (expected for the tenant plane, which is not publicly routed).
    dashboardUrl: string | null
    portForwardCommand: string
}

export interface BackgroundJobsPlane {
    id: string
    displayName: string
    environments: BackgroundJobsCard[]
}

export interface BackgroundJobsResponse {
    planes: BackgroundJobsPlane[]
    dashboardPath: string
    tokenNote: string
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await fetch(path, {
        ...init,
        headers: {
            'Content-Type': 'application/json',
            ...(init?.headers ?? {}),
        },
    })
    if (res.status === 401) {
        // Session expired — redirect to sign-in rather than surfacing a
        // cryptic error. Covers background refetch queries that fire after
        // the 8-hour session window has elapsed.
        window.location.href = '/signin'
        throw new Error('Session expired')
    }
    if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`HTTP ${res.status}: ${text}`)
    }
    return res.json() as Promise<T>
}

export const releaseApi = {
    getPipeline: () => apiFetch<PipelineResponse>('/api/pipeline'),

    promote: (req: PromoteRequest) =>
        apiFetch<PromoteResponse>('/api/promote', {
            method: 'POST',
            body: JSON.stringify(req),
        }),

    getRingHealth: () => apiFetch<HealthResponse>('/api/health/rings'),

    preflight: (source: string, target: string) =>
        apiFetch<PreflightResponse>(`/api/promote/preflight?source=${source}&target=${target}`),

    // AB#2094 — desired (gitops) vs running (pods) image drift per environment.
    getDrift: () => apiFetch<DriftResponse>('/api/pipeline/drift'),

    // AB#2092 — promotion audit trail + one-click rollback.
    getPromotionHistory: (limit = 20) =>
        apiFetch<PromotionHistoryResponse>(`/api/promote/history?limit=${limit}`),

    rollbackPromotion: (promotionId: string, force = false) =>
        apiFetch<RollbackResponse>('/api/promote/rollback', {
            method: 'POST',
            body: JSON.stringify({ promotionId, force }),
        }),

    // AB#2100 — pending-release PR queue with state/age/retry-count.
    getPendingReleases: () => apiFetch<PendingQueueResponse>('/api/releases/pending'),

    retryPendingRelease: (prId: number) =>
        apiFetch<{ prId: number; reset: boolean }>(`/api/releases/pending/${prId}/retry`, { method: 'POST' }),

    getTenantsByRing: () => apiFetch<TenantsByRingResponse>('/api/tenants/by-ring'),

    getTenantDetail: (slug: string) => apiFetch<TenantDetail>(`/api/tenants/${encodeURIComponent(slug)}`),

    getTenantHealth: (slug: string) => apiFetch<TenantHealthResponse>(`/api/tenants/${encodeURIComponent(slug)}/health`),

    getCluster: () => apiFetch<ClusterSnapshot>('/api/cluster'),

    getPodLogs: async (namespaceName: string, podName: string, tailLines = 200): Promise<string> => {
        const res = await fetch(`/api/pods/${encodeURIComponent(namespaceName)}/${encodeURIComponent(podName)}/logs?tailLines=${tailLines}`)
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.text()
    },

    troubleshoot: (logs: string, messages: AiMessage[]) =>
        apiFetch<AiTroubleshootResponse>('/api/ai/troubleshoot', {
            method: 'POST',
            body: JSON.stringify({ logs, messages }),
        }),

    getArgoCdApps: () => apiFetch<ArgoCdAppsResponse>('/api/cluster/argocd/apps'),

    getLandscape: () => apiFetch<LandscapeResponse>('/api/landscape'),

    getBackgroundJobs: () => apiFetch<BackgroundJobsResponse>('/api/background-jobs'),

    restartDeploymentPods: (namespaceName: string, deploymentName: string) =>
        apiFetch<{ namespaceName: string; deploymentName: string; podsDeleted: number }>(
            `/api/cluster/namespaces/${encodeURIComponent(namespaceName)}/deployments/${encodeURIComponent(deploymentName)}/restart-pods`,
            { method: 'POST' }
        ),

    hardRefreshArgoCdApp: (appName: string) =>
        fetch(`/api/cluster/argocd/apps/${encodeURIComponent(appName)}/refresh`, { method: 'POST' })
            .then(async r => {
                if (!r.ok) {
                    const body = await r.json().catch(() => null) as { detail?: string; title?: string } | null
                    throw new Error(body?.detail ?? body?.title ?? `HTTP ${r.status}`)
                }
            }),

    forceSyncArgoCdApp: (appName: string) =>
        fetch(`/api/cluster/argocd/apps/${encodeURIComponent(appName)}/sync`, { method: 'POST' })
            .then(async r => {
                if (!r.ok) {
                    const body = await r.json().catch(() => null) as { detail?: string; title?: string } | null
                    throw new Error(body?.detail ?? body?.title ?? `HTTP ${r.status}`)
                }
            }),

    getClusterHealth: () => apiFetch<ClusterHealthSnapshot>('/api/cluster/health'),

    getPipelineRuns: () => apiFetch<PipelineRunsSnapshot>('/api/pipeline/runs'),

    getMigrations: () => apiFetch<MigrationStatusResponse>('/api/cluster/migrations'),

    getInfraStatus: () => apiFetch<InfraStatusSnapshot>('/api/cluster/infra-status'),

    getNodeMetrics: () => apiFetch<NodeMetricsSnapshot>('/api/cluster/node-metrics'),

    getPodMetrics: () => apiFetch<PodMetricsSnapshot>('/api/cluster/pod-metrics'),

    getHpa: () => apiFetch<HpaSnapshot>('/api/cluster/hpa'),

    getSecretExpiry: () => apiFetch<SecretExpirySnapshot>('/api/secrets/expiry'),

    getPipelineRunHistory: (deployable: string, count = 10) =>
        apiFetch<PipelineRunInfo[]>(`/api/pipeline/runs/${encodeURIComponent(deployable)}/history?count=${count}`),

    drainNode: (nodeName: string) =>
        apiFetch<{ nodeName: string; podsEvicted: number }>(
            `/api/cluster/nodes/${encodeURIComponent(nodeName)}/drain`,
            { method: 'POST' }
        ),

    restartNode: (nodeName: string) =>
        fetch(`/api/cluster/nodes/${encodeURIComponent(nodeName)}/restart`, { method: 'POST' })
            .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`) }),

    uncordonNode: (nodeName: string) =>
        apiFetch<{ nodeName: string }>(`/api/cluster/nodes/${encodeURIComponent(nodeName)}/uncordon`, { method: 'POST' }),

    getBranches: () => apiFetch<BranchDiagramResponse>('/api/branches'),

    getReleasesParkingLot: () => apiFetch<WorkItemsResponse>('/api/releases/parking-lot'),

    getReleasesBacklog: () => apiFetch<WorkItemsResponse>('/api/releases/backlog'),

    getReleasesInProgress: () => apiFetch<WorkItemsResponse>('/api/releases/in-progress'),

    getReleasesReady: () => apiFetch<WorkItemsResponse>('/api/releases/ready'),

    startWork: (req: StartWorkRequest) =>
        apiFetch<StartWorkResult>('/api/releases/start-work', {
            method: 'POST',
            body: JSON.stringify(req),
        }),

    raisePr: (req: RaisePrRequest) =>
        apiFetch<RaisePrResult>('/api/releases/raise-pr', {
            method: 'POST',
            body: JSON.stringify(req),
        }),

    addToRelease: (req: AddToReleaseRequest) =>
        apiFetch<AddToReleaseResult>('/api/releases/add-to-release', {
            method: 'POST',
            body: JSON.stringify(req),
        }),

    getActiveRelease: () => apiFetch<ActiveReleaseInfo>('/api/releases/active'),

    getReleasePipelineStatus: (branch: string) =>
        apiFetch<ReleasePipelineStatus>(`/api/releases/active/pipeline-status?branch=${encodeURIComponent(branch)}`),

    getReleaseDevSyncStatus: () => apiFetch<ReleaseDevSyncStatus>('/api/releases/active/dev-sync-status'),

    getReleaseHistory: () => apiFetch<ReleaseHistoryResponse>('/api/releases/history'),

    getWorkItemDetail: (id: number) => apiFetch<WorkItemDetail>(`/api/releases/work-items/${id}`),

    sendBack: (req: SendBackRequest) =>
        apiFetch<void>('/api/releases/send-back', {
            method: 'POST',
            body: JSON.stringify(req),
        }),

    markReleased: (id: number) =>
        apiFetch<{ workItemId: number }>(`/api/releases/items/${id}/mark-released`, { method: 'POST' }),

    closeWorkItem: (id: number, reason?: string) =>
        apiFetch<{ workItemId: number }>(`/api/releases/items/${id}/close`, {
            method: 'POST',
            body: JSON.stringify({ reason: reason ?? null }),
        }),

    bulkStartWork: (req: BulkStartWorkRequest) =>
        apiFetch<BulkStartWorkResult>('/api/releases/bulk-start-work', {
            method: 'POST',
            body: JSON.stringify(req),
        }),

    startEpic: (req: StartEpicRequest) =>
        apiFetch<StartEpicResult>('/api/releases/start-epic', {
            method: 'POST',
            body: JSON.stringify(req),
        }),

    addEpicToRelease: (req: AddEpicToReleaseRequest) =>
        apiFetch<AddEpicToReleaseResult>('/api/releases/add-epic-to-release', {
            method: 'POST',
            body: JSON.stringify(req),
        }),

    takeSpec: (workItemId: number) =>
        apiFetch<TakeSpecResult>(`/api/releases/parking-lot/${workItemId}/take`, { method: 'POST' }),

    promoteToBacklog: (workItemId: number) =>
        apiFetch<{ workItemId: number; promoted: boolean }>(
            `/api/releases/parking-lot/${workItemId}/promote-to-backlog`,
            { method: 'POST' }
        ),

    sendToParkingLot: (workItemId: number) =>
        apiFetch<{ workItemId: number }>(
            `/api/releases/backlog/${workItemId}/send-to-parking-lot`,
            { method: 'POST' }
        ),

    assignSpec: (workItemId: number, req: AssignSpecRequest) =>
        apiFetch<TakeSpecResult>(`/api/releases/parking-lot/${workItemId}/assign`, {
            method: 'POST',
            body: JSON.stringify(req),
        }),

    assignBacklogItem: (workItemId: number, req: AssignSpecRequest) =>
        apiFetch<TakeSpecResult>(`/api/releases/backlog/${workItemId}/assign`, {
            method: 'POST',
            body: JSON.stringify(req),
        }),

    getTeamMembers: () => apiFetch<TeamMembersResponse>('/api/releases/team-members'),

    getPipelineJourney: (workItemId: number, workItemType: string) =>
        apiFetch<WorkItemPipelineJourney>(
            `/api/releases/items/${workItemId}/pipeline-journey?workItemType=${encodeURIComponent(workItemType)}`
        ),
    getDeliveryStatus: (workItemId: number, workItemType: string) =>
        apiFetch<WorkItemDeliveryStatus>(`/api/releases/items/${workItemId}/delivery-status?workItemType=${encodeURIComponent(workItemType)}`),

    listTokens: () => apiFetch<DevTokenInfo[]>('/api/tokens'),

    createToken: (name: string) =>
        apiFetch<CreateTokenResponse>('/api/tokens', {
            method: 'POST',
            body: JSON.stringify({ name }),
        }),

    reissueToken: (id: string) =>
        apiFetch<CreateTokenResponse>(`/api/tokens/${id}/reissue`, { method: 'POST' }),

    revokeToken: (id: string) =>
        apiFetch<void>(`/api/tokens/${id}`, { method: 'DELETE' }),

    getImages: () => apiFetch<ImagesResponse>('/api/releases/images'),

    getMe: () => apiFetch<{ name: string; email: string }>('/api/me'),

    // Sprint lifecycle
    getCurrentSprint: () => apiFetch<SprintStatusResponse | null>('/api/sprint/current'),

    getActiveSprints: () => apiFetch<SprintStatusResponse[]>('/api/sprint/active'),

    getNextSprintNumber: () => apiFetch<{ nextNumber: number }>('/api/sprint/next-number'),

    planSprint: (req: PlanSprintRequest) =>
        apiFetch<SprintInfo>('/api/sprint/plan', { method: 'POST', body: JSON.stringify(req) }),

    startSprint: (sprintNumber: number) =>
        apiFetch<SprintInfo>('/api/sprint/start', { method: 'POST', body: JSON.stringify({ sprintNumber }) }),

    addSprintToRelease: (req: AddSprintToReleaseRequest) =>
        apiFetch<AddSprintToReleaseResult>('/api/sprint/add-to-release', { method: 'POST', body: JSON.stringify(req) }),

    addItemsToSprint: (sprintNumber: number, req: AddItemsToSprintRequest) =>
        apiFetch<{ sprintNumber: number; count: number }>(
            `/api/sprint/${sprintNumber}/add-items`,
            { method: 'POST', body: JSON.stringify(req) }
        ),

    removeItemsFromSprint: (req: AddItemsToSprintRequest) =>
        apiFetch<{ count: number }>(
            '/api/sprint/remove-items',
            { method: 'POST', body: JSON.stringify(req) }
        ),

    deleteSprint: (sprintNumber: number) =>
        apiFetch<{ sprintNumber: number; deleted: boolean }>(
            `/api/sprint/${sprintNumber}`,
            { method: 'DELETE' }
        ),

    lockSprint: (sprintNumber: number) =>
        apiFetch<{ sprintNumber: number; locked: boolean }>(
            `/api/sprint/${sprintNumber}/lock`,
            { method: 'POST' }
        ),

    unlockSprint: (sprintNumber: number) =>
        apiFetch<{ sprintNumber: number; locked: boolean }>(
            `/api/sprint/${sprintNumber}/unlock`,
            { method: 'POST' }
        ),

    getSprintHistory: (limit?: number) => apiFetch<SprintHistorySummary[]>(`/api/sprint/history${limit !== undefined ? `?limit=${limit}` : ''}`),

    getSprintTestPlan: (sprintNumber: number) =>
        apiFetch<SprintTestPlan>(`/api/sprint/history/${sprintNumber}/test-plan`),

    getAvailableSprints: () => apiFetch<AvailableSprintsResponse>('/api/sprint/available'),

    retrySprintPipeline: (sprintNumber: number) =>
        apiFetch<{ sprintNumber: number; buildId: number; buildUrl: string | null }>(
            `/api/sprint/history/${sprintNumber}/retry-pipeline`,
            { method: 'POST' }
        ),

    getSprintSession: (sprintNumber: number) =>
        apiFetch<SprintSessionResponse>(`/api/sprint/${sprintNumber}/session`),

    startSession: (sprintNumber: number) =>
        apiFetch<{ sessionId: string }>(`/api/sprint/${sprintNumber}/session/start`, { method: 'POST', body: '{}' }),

    pushSessionChunk: (sessionId: string, content: string, chunkType: string, sprintNumber: number) =>
        apiFetch<void>(`/api/session/${sessionId}/chunks`, {
            method: 'POST',
            body: JSON.stringify({ content, chunkType, sprintNumber }),
        }),

    endSession: (sessionId: string, sprintNumber: number) =>
        apiFetch<void>(`/api/session/${sessionId}/end`, {
            method: 'POST',
            body: JSON.stringify({ sprintNumber }),
        }),
}

export interface TenantRingEntry {
    tenantId: string
    slug: string
    displayName: string
    status: string
}

export interface RingTenantGroup {
    ring: string
    tenants: TenantRingEntry[]
}

export interface TenantsByRingResponse {
    rings: RingTenantGroup[]
    fetchedAt: string
}

export interface TenantDetail {
    tenantId: string
    slug: string
    displayName: string
    status: string
    ring: string
    adminEmail: string
    createdAt: string
    updatedAt: string
    companyCount: number
}

export interface TenantHealthResponse {
    slug: string
    status: string
    requestsPerMin: number | null
    errorPct: number | null
    p95Ms: number | null
    asOf: string
}

// ---- Cluster ----

export interface PodSnapshot {
    name: string
    phase: string
    status: string
    ready: boolean
    restartCount: number
    image?: string
    startTime?: string
}

export interface DeploymentSnapshot {
    name: string
    readyReplicas: number
    desiredReplicas: number
    pods: PodSnapshot[]
}

export interface NamespaceSnapshot {
    name: string
    deployments: DeploymentSnapshot[]
}

export interface NodeInfo {
    name: string
    status: string
    role: string
    kubernetesVersion: string
    unschedulable: boolean
}

export interface ClusterSnapshot {
    clusterName: string
    nodes: NodeInfo[]
    namespaces: NamespaceSnapshot[]
    fetchedAt: string
}

// ---- ArgoCD ----

export interface ArgoCdApp {
    name: string
    destinationNamespace: string
    syncStatus: string
    healthStatus: string
    revision: string | null
    lastSyncTime: string | null
    repoUrl: string | null
    repoPath: string | null
    lastSyncError: string | null
}

export interface ExternalSecretStatus {
    name: string
    namespace: string
    ready: boolean
    reason: string | null
    message: string | null
}

export interface ClusterHealthSnapshot {
    externalSecrets: ExternalSecretStatus[]
    argoCdApps: ArgoCdApp[]
    fetchedAt: string
}

export interface ArgoCdAppsResponse {
    apps: ArgoCdApp[]
    fetchedAt: string
}

// ---- Pipeline runs ----

export interface PipelineRunInfo {
    deployable: string
    pipelineName: string
    state: string
    result: string | null
    webUrl: string | null
    finishedAt: string | null
    startedAt: string | null
}

export interface PipelineRunsSnapshot {
    runs: PipelineRunInfo[]
    fetchedAt: string
}

// ---- Migrations ----

export interface MigrationStatusResponse {
    applied: string[]
    pending: string[]
    fetchedAt: string
}

// ---- Node metrics ----

export interface NodeMetric {
    nodeName: string
    cpuMillicores: number
    cpuCapacityMillicores: number
    memoryMiB: number
    memoryCapacityMiB: number
}

export interface NodeMetricsSnapshot {
    nodes: NodeMetric[]
    fetchedAt: string
}

// ---- Pod metrics ----

export interface PodMetric {
    podName: string
    namespace: string
    nodeName: string
    cpuMillicores: number
    memoryMiB: number
}

export interface PodMetricsSnapshot {
    pods: PodMetric[]
    fetchedAt: string
}

// ---- HPA ----

export interface HpaInfo {
    namespace: string
    deploymentName: string
    minReplicas: number
    maxReplicas: number
    targetCpuPercent: number | null
}

export interface HpaSnapshot {
    hpas: HpaInfo[]
    fetchedAt: string
}

// ---- Secrets expiry ----

export interface SecretExpiryInfo {
    name: string
    expiresOn: string | null
    enabled: boolean
    updatedOn: string | null
}

export interface SecretExpirySnapshot {
    secrets: SecretExpiryInfo[]
    fetchedAt: string
}

// ---- Infra status dots ----

export type DotStatus = 'Healthy' | 'Warning' | 'Error' | 'Unknown'

export interface StatusDot {
    id: string
    label: string
    status: DotStatus
    detail: string | null
}

export interface StatusGroup {
    id: string
    label: string
    dots: StatusDot[]
}

export interface InfraStatusSnapshot {
    groups: StatusGroup[]
    fetchedAt: string
}

// ---- AI ----

export interface AiMessage {
    role: 'user' | 'assistant'
    content: string
}

export interface AiTroubleshootResponse {
    reply: string
}

// ---- Releases / work items ----

export interface WorkItemSummary {
    id: number
    title: string
    workItemType: string
    state: string
    assignedTo: string | null
    tags: string | null
    createdDate: string | null
    priority: number | null
    epicId: number | null
    epicTitle: string | null
    iterationPath: string | null
}

export interface WorkItemsResponse {
    items: WorkItemSummary[]
    fetchedAt: string
}

export interface StartWorkRequest {
    workItemId: number
    workItemType: string
}

export interface StartWorkResult {
    workItemId: number
    branchName: string
    workItemUrl: string | null
}

export interface RaisePrRequest {
    workItemId: number
    workItemType: string
}

export interface RaisePrResult {
    workItemId: number
    prUrl: string
    prId: number
}

export interface AddToReleaseRequest {
    workItemId: number
    workItemType: string
}

export interface AddToReleaseResult {
    workItemId: number
    releaseBranch: string
    branchCreated: boolean
    lastCommitSha: string | null
    cherryPickStatus: string  // "already-merged" | "pr-raised" | "succeeded" | "conflict" | "failed" | "timeout"
    prUrl: string | null
}

export interface ReleasePipelineStatus {
    buildId: number | null
    state: string        // "none" | "notStarted" | "inProgress" | "completed"
    result: string | null  // "succeeded" | "failed" | "canceled" | null while running
    webUrl: string | null
    startedAt: string | null
    finishedAt: string | null
    sourceVersion: string | null
    fetchedAt: string
}

export interface ReleaseAppSyncStatus {
    appName: string
    syncStatus: string       // "Synced" | "OutOfSync" | "Unknown"
    operationPhase?: string | null  // "Running" | "Succeeded" | "Failed" | "Error" | null
    healthStatus: string  // "Healthy" | "Degraded" | "Progressing" | "Unknown"
    revision: string | null
    lastSyncTime: string | null
    lastSyncError: string | null
}

export interface ReleaseDevSyncStatus {
    apps: ReleaseAppSyncStatus[]
    fetchedAt: string
}

export interface ReleaseWorkItem {
    id: number
    title: string
    workItemType: string
    assignedTo: string | null
}

export interface RingLevelStatus {
    env: string        // "dev" | "qa" | "ring0" | "ring1" | "ring2"
    synced: boolean
    healthy: boolean
}

export interface ActiveReleaseInfo {
    releaseBranch: string | null
    items: ReleaseWorkItem[]
    ringLevels: RingLevelStatus[]
    fetchedAt: string
}

export interface ReleaseBranchSummary {
    name: string
    dateLabel: string
}

export interface ReleaseHistoryResponse {
    branches: ReleaseBranchSummary[]
    fetchedAt: string
}

export interface SendBackRequest {
    workItemId: number
}

// ── Bulk Start Work ────────────────────────────────────────────────────────────

export interface BulkStartWorkItem {
    workItemId: number
    workItemType: string
}

export interface BulkStartWorkRequest {
    items: BulkStartWorkItem[]
}

export interface BulkStartWorkResult {
    results: StartWorkResult[]
    successCount: number
    failureCount: number
}

// ── Epic-level Start Work ──────────────────────────────────────────────────────

export interface StartEpicRequest {
    epicId: number
}

export interface StartEpicResult {
    epicId: number
    results: StartWorkResult[]
    successCount: number
    failureCount: number
}

// ── Epic-level Add to Release ─────────────────────────────────────────────────

export interface AddEpicToReleaseRequest {
    epicId: number
}

export interface AddEpicToReleaseResult {
    epicId: number
    results: AddToReleaseResult[]
    releaseBranch: string
    successCount: number
    failureCount: number
}

// ── Parking-lot spec assignment ────────────────────────────────────────────────

export interface TakeSpecResult {
    workItemId: number
    assignedTo: string | null
}

export interface AssignSpecRequest {
    assignTo: string
}

// ── Developer tokens ──────────────────────────────────────────────────────────

export interface DevTokenInfo {
    id: string
    name: string
    developerEmail: string
    createdAt: string
    lastUsedAt: string | null
}

export interface CreateTokenResponse {
    id: string
    name: string
    developerEmail: string
    createdAt: string
    plaintext: string
    warning: string
}

// ── Pipeline journey ──────────────────────────────────────────────────────────

export type JourneyStageStatus = 'Pending' | 'InProgress' | 'Success' | 'Failed' | 'Skipped'

export interface WorkItemDeliveryStatus {
    workItemId: number
    mergeState: string
    targetBranch: string | null
    prUrl: string | null
    builds: { id: number; name: string; state: string; result: string | null; url: string | null }[]
    fetchedAt: string
}

export interface JourneyStage {
    id: string
    label: string
    status: JourneyStageStatus
    detail: string | null
    url: string | null
    timestamp: string | null
}

export interface WorkItemPipelineJourney {
    workItemId: number
    branchName: string
    stages: JourneyStage[]
    fetchedAt: string
}

// ── Team members ──────────────────────────────────────────────────────────────

export interface TeamMember {
    id: string
    displayName: string
    uniqueName: string
}

export interface TeamMembersResponse {
    members: TeamMember[]
    fetchedAt: string
}

// ── Images ────────────────────────────────────────────────────────────────────

export interface ImageWorkItem {
    id: number
    title: string
    workItemType: string
    state: string
}

export interface ImageSummary {
    imageRef: string
    sourceVersion: string
    buildDate: string
    pipelines: string[]
    workItems: ImageWorkItem[]
}

export interface ImagesResponse {
    images: ImageSummary[]
    fetchedAt: string
}

// ── Sprint ────────────────────────────────────────────────────────────────────

export interface SprintInfo {
    number: number
    iterationPath: string
    branchName: string
    branchExists: boolean
    status: string          // 'Queued' | 'Started' | 'Released'
    prRaisedAt: string | null
    iterationExists: boolean
    workingDeveloper: string | null
    locked: boolean
}

export interface SprintItemStatus {
    epicId?: number | null
    epicTitle?: string | null
    workItemId: number
    title: string
    workItemType: string
    adoState: string
    mergedToSprint: boolean
    deferred: boolean
    tags: string | null
}

export interface SprintPipelineRun {
    name: string
    buildNumber: string | null
    status: string
    result: string | null
    webUrl: string | null
}

export interface SprintReleasePipeline {
    prStatus: string        // "active" | "completed" | "abandoned"
    prMergeCommitSha: string | null
    runs: SprintPipelineRun[]
    devDeployed?: boolean
    qaDeployed?: boolean
    gitOpsPrStatus?: string | null   // "active" | "completed" | "abandoned" | "none" | null
    gitOpsMerged?: boolean
    prMergeStatus?: string | null    // "succeeded" | "rejectedByPolicy" | "failure" | "conflicts" | "queued" | null
}

export interface SprintStatusResponse {
    sprint: SprintInfo
    items: SprintItemStatus[]
    releasePrId: number | null
    releasePrUrl: string | null
    fetchedAt: string
    pipeline?: SprintReleasePipeline | null
}

export interface PlanSprintRequest {
    sprintNumber: number
    workItemIds: number[]
}

export interface AddSprintToReleaseRequest {
    sprintNumber: number
    deferItemIds?: number[]
}

export interface AddItemsToSprintRequest {
    workItemIds: number[]
}

export interface AvailableSprintsResponse {
    sprintNumbers: number[]
    fetchedAt: string
}

export interface SprintHistoryItem {
    id: number
    title: string
    workItemType: string
    state: string
}

export interface SprintHistorySummary {
    sprintNumber: number
    prId: number
    prUrl: string
    mergedAt: string
    itemCount: number
    items: SprintHistoryItem[]
    pipeline?: SprintReleasePipeline | null
}

export interface SprintTestPlanItem {
    id: number
    title: string
    workItemType: string
    acceptanceCriteria: string | null
}

export interface SprintTestPlan {
    sprintNumber: number
    prId: number
    prUrl: string
    mergedAt: string
    items: SprintTestPlanItem[]
}

export interface SpecHubProvenance {
    specId: string
    specTitle: string
    authorEmail: string
    promotedByEmail: string
    promotedAt: string
}

export interface SpecBody {
    title: string
    bodyMd: string
}

export interface WorkItemDependency {
    id: number
    title: string
    state: string
}

export interface WorkItemDetail {
    id: number
    title: string
    workItemType: string
    state: string
    assignedTo: string | null
    tags: string | null
    description: string | null
    acceptanceCriteria: string | null
    createdBy: string | null
    specHub: SpecHubProvenance | null
    specHubBody: SpecBody | null
    requires: WorkItemDependency[] | null
    requiredBy: WorkItemDependency[] | null
}

export interface AddSprintToReleaseResult {
    sprintNumber: number
    prUrl: string
    prId: number
    deferredCount: number
    branchName: string
}

// ── Branch diagram ─────────────────────────────────────────────────────────────

export interface BranchInfo {
    name: string
    shortSha: string
    commitMessage: string
    authorName: string
    committedAt: string
    aheadCount: number
    behindCount: number
    branchType: string
    isStale: boolean
}

export interface BranchDiagramResponse {
    branches: BranchInfo[]
    fetchedAt: string
}

// ── Sprint sessions ───────────────────────────────────────────────────────────

export interface ActiveSprintSession {
    id: string
    sprintNumber: number
    initiatorEmail: string
    startedAt: string
    endedAt: string | null
    isActive: boolean
}

export interface SprintSessionChunk {
    id: number
    sessionId: string
    content: string
    chunkType: string
    createdAt: string
}

export interface SprintSessionResponse {
    session: ActiveSprintSession
    chunks: SprintSessionChunk[]
}
